import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import {
  AppButton,
  PasswordCriteria,
  StickyHeaderNavigation,
} from '@/components'
import { BaseView, BaseText } from '@/components/base'
import { TextInputForm } from '@/components/form'
import { inputLimits } from '@/constants/inputLimits'
import { useRecoverPassword } from '@/domain'
import { usePasswordCriteriaControl, useUserAuthSession } from '@/hooks'
import { useAuthScopedStore } from '@/store/scoped'
import { ICreatePasswordSchema, useCreatePasswordSchema } from '@/schemas'

export const CreateNewPasswordScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const verifyToken = useAuthScopedStore(
    (state) => state.recoverPasswordForm.verifyToken,
  )
  const { startSession } = useUserAuthSession()

  const {
    allCriteriaMet,
    setAllCriteriaMet,
    checkCriteriaOnBlur,
    showErrorState,
  } = usePasswordCriteriaControl()

  const createPasswordSchema = useCreatePasswordSchema()

  const {
    handleSubmit,
    control,
    formState,
    watch,
    setError,
    clearErrors,
    trigger,
  } = useForm<ICreatePasswordSchema>({
    resolver: zodResolver(createPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const password = watch('password')
  const confirmPassword = watch('confirmPassword')

  const { mutateAsync: recoverPassword, isPending } = useRecoverPassword({
    onSuccess: async ({ access_token, refresh_token }) => {
      await startSession({
        token: access_token,
        refreshToken: refresh_token,
      })

      toast.success(t('translation:successResetPassword'))
      navigate('/dashboard', { replace: true })
    },
    onError: ({ errorMessage }) => {
      toast.error(errorMessage || t('api:errors.somethingWrong'))
    },
  })

  const canSubmit = () =>
    Boolean(
      password &&
        confirmPassword &&
        password === confirmPassword &&
        allCriteriaMet &&
        formState.isValid,
    )

  const handleSubmitForm = async (formData: ICreatePasswordSchema) => {
    if (!verifyToken) {
      toast.error('Verification token is missing')
      return
    }

    await recoverPassword({
      password: formData.password,
      verify_token: verifyToken,
    })
  }

  const handleBlurNewPassword = () => {
    // Mirror RN: when leaving password, validate criteria and match if confirm exists
    if (confirmPassword) {
      trigger('confirmPassword')
    }
    checkCriteriaOnBlur()
  }

  useEffect(() => {
    if (!verifyToken) {
      navigate('/auth/forgot-password/verification-code', { replace: true })
    }
  }, [verifyToken, navigate])

  return (
    <>
      <BaseView className="h-full flex flex-col" px={12} py={6}>
        <StickyHeaderNavigation />
        <BaseView className="flex-1" gap={28}>
          <BaseView gap={20}>
            <BaseText
              fontSize={32}
              fontFamily="semibold"
              translationKey="screens:security.createNewPassword.title"
            />

            <BaseView gap={20}>
              <BaseView gap={12}>
                <TextInputForm
                  control={control}
                  name="password"
                  label={t('form:labels.enterNewPassword')}
                  secureTextEntry
                  textContentType="newPassword"
                  maxLength={inputLimits.password.max}
                  onBlur={handleBlurNewPassword}
                  onChange={(e) => {
                    const value = e.target.value
                    // If confirm has value, re-validate match to show/hide mismatch promptly
                    if (
                      value &&
                      confirmPassword &&
                      confirmPassword.length >= 1
                    ) {
                      trigger('confirmPassword')
                    }
                  }}
                  className="ym-hide-content"
                />

                <TextInputForm
                  control={control}
                  name="confirmPassword"
                  label={t('form:labels.repeatNewPassword')}
                  secureTextEntry
                  textContentType="newPassword"
                  maxLength={inputLimits.password.max}
                  onBlur={() => {
                    // Validate on blur to surface mismatch warning like RN
                    if (password !== confirmPassword) {
                      setError('confirmPassword', {
                        type: 'manual',
                        message: t('api:validationErrors.noMatch'),
                      })
                    } else {
                      clearErrors('confirmPassword')
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value
                    // Live clear/set mismatch error to mirror RN UX
                    if (password && value && password !== value) {
                      setError('confirmPassword', {
                        type: 'manual',
                        message: t('api:validationErrors.noMatch'),
                      })
                    } else if (password === value) {
                      clearErrors('confirmPassword')
                    }
                  }}
                  className="ym-hide-content"
                />
              </BaseView>

              <PasswordCriteria
                password={password || ''}
                setAllCriteriaMet={setAllCriteriaMet}
                showErrorState={showErrorState}
              />
            </BaseView>
          </BaseView>
        </BaseView>

        <BaseView mt="auto" pb={12}>
          <AppButton
            label={t('form:actions.resetPassword')}
            disabled={!canSubmit()}
            isLoading={isPending}
            onClick={handleSubmit(handleSubmitForm)}
          />
        </BaseView>
      </BaseView>
    </>
  )
}
